import React, { useState, useEffect } from 'react';
import { useAuth } from '../auth/AuthContext';
import EventoService from '../services/eventoService';
import notificacionService from '../services/notificacionService';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Badge, Spinner } from 'react-bootstrap';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CarruselEliptico from '../components/CarruselEliptico';
import { iconoPorOrigen } from '../utils/notificacionUtils';
import PageHeader from '../components/ui/PageHeader';
import '../style/pages/HomePage.css';

const CANTIDAD_NOTIFICACIONES_HOME = 5;

const HomePage = () => {
    const { user, puede } = useAuth();
    // Mismo permiso que la sección RECEPCIÓN del menú lateral (NavegacionLateral.js).
    const puedeVerRecepcion = puede('recepcion.ver');
    const [proximosEventos, setProximosEventos] = useState([]);
    const [loading, setLoading] = useState(true);
    // Estado propio y separado del de eventos: un fallo al cargar notificaciones no
    // debe bloquear ni tapar el panel de Próximos Eventos.
    const [notificacionesRecientes, setNotificacionesRecientes] = useState([]);
    const [loadingNotificaciones, setLoadingNotificaciones] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const cargarEventos = async () => {
            try {
                const response = await EventoService.getEventos();
                const eventos = response.data;

                // 1. Filtrar eventos futuros o de hoy
                const hoy = new Date();
                hoy.setHours(0, 0, 0, 0);

                const eventosFuturos = eventos.filter(e => {
                    // Convertir fecha string a objeto Date asegurando compatibilidad
                    const fechaStr = e.fecha.includes('T') ? e.fecha : `${e.fecha}T00:00:00`;
                    const fechaEvento = new Date(fechaStr);
                    return fechaEvento >= hoy;
                });

                // 2. Ordenar por fecha y hora (los más cercanos primero)
                eventosFuturos.sort((a, b) => {
                    const fechaA = new Date(`${a.fecha}T${a.horaInicio || '00:00'}`);
                    const fechaB = new Date(`${b.fecha}T${b.horaInicio || '00:00'}`);
                    return fechaA - fechaB;
                });

                // 3. Tomar solo los primeros 4 para no saturar el inicio
                setProximosEventos(eventosFuturos.slice(0, 4));

            } catch (error) {
                console.error("Error al cargar eventos:", error);
            } finally {
                setLoading(false);
            }
        };

        cargarEventos();
    }, []);

    useEffect(() => {
        const cargarNotificaciones = async () => {
            try {
                const response = await notificacionService.getHistorial();
                const data = response?.data ?? response;
                const lista = Array.isArray(data) ? data : [];
                lista.sort((a, b) => new Date(b.fechaProgramada) - new Date(a.fechaProgramada));
                setNotificacionesRecientes(lista.slice(0, CANTIDAD_NOTIFICACIONES_HOME));
            } catch (error) {
                console.error('Error al cargar notificaciones:', error);
            } finally {
                setLoadingNotificaciones(false);
            }
        };

        cargarNotificaciones();
    }, []);

    const formatearFecha = (fechaStr) => {
        if (!fechaStr) return '';
        const [year, month, day] = fechaStr.split('-');
        return `${day}/${month}/${year}`;
    };

    const formatearFechaNotificacion = (fechaStr) => {
        if (!fechaStr) return '';
        const [year, month, day] = fechaStr.split('T')[0].split('-');
        return `${day}/${month}/${year}`;
    };

    // Igual que NotificacionCampana.abrirNotificacion: marca como leída al tocar
    // (optimista) y navega directo al trámite si lo tiene asociado.
    const handleClickNotificacion = (notif) => {
        if (!notif.leido) {
            setNotificacionesRecientes((prev) =>
                prev.map((n) => (n.id === notif.id ? { ...n, leido: true } : n))
            );
            notificacionService.marcarComoLeidaUsuario(notif.id).catch((err) =>
                console.error('Error al marcar notificación como leída:', err)
            );
        }
        if (notif.idTramite) {
            navigate(`/tramites/${notif.idTramite}`);
        }
    };

    // Función para decidir a dónde navegar al hacer clic en una tarjeta
    const handleNavegacionEvento = (evento) => {
        if (evento.idTramite) {
            // El backend ya resuelve la ruta correcta según el tipo real del trámite
            // (ART/RTI/Jubilación/PUAM/Plan de Pago/Reajuste); "/tramites/{id}" (legacy RTI)
            // redirige solo vía TramiteLegacyRedirect.
            navigate(evento.rutaTramite || `/tramites/${evento.idTramite}`);
        } else {
            // Si es un evento suelto, ir al calendario
            navigate('/calendario');
        }
    };


    return (
        <div className="homepage-container">
            <PageHeader
                variante="plano"
                icono="house-door"
                titulo={`¡Hola, ${user?.nombre || 'Usuario'}!`}
                descripcion="Bienvenido a tu panel de control. Aquí tienes lo más importante para hoy."
            />

                    {/* --- ACCESOS RÁPIDOS (CARRUSEL) --- */}
                    <div className="carrusel-home-section">
                        <CarruselEliptico />
                    </div>

                    {/* --- ACCESOS DIRECTOS (RECEPCIÓN) --- */}
                    {puedeVerRecepcion && (
                        <div className="eventos-section">
                            <div className="eventos-header-row">
                                <h2 className="eventos-section-title">
                                    <i className="bi bi-inbox-fill me-2"></i>
                                    Accesos Directos
                                </h2>
                            </div>

                            <div className="accesos-rapidos-home accesos-rapidos-grid">
                                <div
                                    className="acceso-card acceso-card-turnero"
                                    onClick={() => navigate('/recepcion/turnero')}
                                >
                                    <i className="bi bi-calendar3 acceso-card-icon"></i>
                                    <div className="acceso-card-body">
                                        <h5>Turnero</h5>
                                        <p>Gestionar los turnos del día</p>
                                    </div>
                                    <i className="bi bi-arrow-right-short acceso-card-arrow"></i>
                                </div>
                                <div
                                    className="acceso-card acceso-card-panel-diario"
                                    onClick={() => navigate('/recepcion')}
                                >
                                    <i className="bi bi-speedometer2 acceso-card-icon"></i>
                                    <div className="acceso-card-body">
                                        <h5>Panel Diario</h5>
                                        <p>Vista general de Recepción</p>
                                    </div>
                                    <i className="bi bi-arrow-right-short acceso-card-arrow"></i>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* --- SECCIÓN DE NOTIFICACIONES --- */}
                    <div className="eventos-section">
                        <div className="eventos-header-row">
                            <h2 className="eventos-section-title">
                                <i className="bi bi-bell-fill me-2"></i>
                                Notificaciones
                            </h2>
                            <button
                                className="btn-ver-calendario-home"
                                onClick={() => navigate('/notificaciones')}
                            >
                                Ver todas
                                <i className="bi bi-arrow-right ms-2"></i>
                            </button>
                        </div>

                        {loadingNotificaciones ? (
                            <div className="loading-state">
                                <Spinner animation="border" variant="primary" />
                                <p className="mt-3">Cargando notificaciones...</p>
                            </div>
                        ) : notificacionesRecientes.length === 0 ? (
                            <div className="empty-state-eventos">
                                <i className="bi bi-bell-slash"></i>
                                <h5>Sin notificaciones</h5>
                                <p>Todavía no recibiste ninguna notificación.</p>
                            </div>
                        ) : (
                            <Row xs={1} md={2} lg={3} className="g-3">
                                {notificacionesRecientes.map((notif) => {
                                    const { icon, color } = iconoPorOrigen(notif);
                                    const leida = notif.leido === true;
                                    return (
                                        <Col key={notif.id}>
                                            <div
                                                className={`notificacion-card-home ${leida ? '' : 'no-leida'}`}
                                                onClick={() => handleClickNotificacion(notif)}
                                            >
                                                <div className="notificacion-card-home-header">
                                                    <div
                                                        className="notificacion-card-home-icono"
                                                        style={{ color: leida ? '#94a3b8' : color, background: `${leida ? '#94a3b8' : color}18` }}
                                                    >
                                                        <i className={`bi ${icon}`}></i>
                                                    </div>
                                                    {!leida && <span className="notificacion-dot-no-leida" />}
                                                </div>

                                                <p className="notificacion-mensaje-home">{notif.mensaje}</p>

                                                <div className="notificacion-meta-home">
                                                    <span className="info-item-home">
                                                        <i className="bi bi-calendar3 me-2"></i>
                                                        {formatearFechaNotificacion(notif.fechaProgramada)}
                                                    </span>
                                                </div>
                                            </div>
                                        </Col>
                                    );
                                })}
                            </Row>
                        )}
                    </div>

                    {/* --- SECCIÓN DE PRÓXIMOS EVENTOS --- */}
            <div className="eventos-section">
                <div className="eventos-header-row">
                    <h2 className="eventos-section-title">
                        <i className="bi bi-calendar-event me-2"></i>
                        Próximos Eventos
                    </h2>
                    <button
                        className="btn-ver-calendario-home"
                        onClick={() => navigate('/calendario')}
                    >
                        Ver Calendario Completo
                        <i className="bi bi-arrow-right ms-2"></i>
                    </button>
                </div>

                {loading ? (
                    <div className="loading-state">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-3">Cargando tu agenda...</p>
                    </div>
                ) : proximosEventos.length === 0 ? (
                    <div className="empty-state-eventos">
                        <i className="bi bi-calendar-check"></i>
                        <h5>¡Agenda despejada!</h5>
                        <p>No tienes eventos programados para los próximos días.</p>
                    </div>
                ) : (
                    <Row xs={1} md={2} lg={4} className="g-3">
                        {proximosEventos.map((evento) => (
                            <Col key={evento.idEvento}>
                                <div
                                    className="evento-card-home"
                                    onClick={() => handleNavegacionEvento(evento)}
                                >
                                    <div className="evento-badges">
                                        <Badge bg="warning" text="dark" className="badge-tipo-home">
                                            {evento.tipoEvento || 'Evento'}
                                        </Badge>
                                        {evento.idTramite && (
                                            <Badge className="badge-tramite-home">
                                                Trámite #{evento.idTramite}
                                            </Badge>
                                        )}
                                    </div>

                                    <h6 className="evento-titulo-home" title={evento.titulo}>
                                        {evento.titulo}
                                    </h6>

                                    <div className="evento-info-home">
                                        <div className="info-item-home">
                                            <CalendarMonthIcon fontSize="small" className="me-2" />
                                            {formatearFecha(evento.fecha)}
                                        </div>
                                        <div className="info-item-home">
                                            <AccessTimeIcon fontSize="small" className="me-2" />
                                            {evento.todoElDia ? 'Todo el día' : `${evento.horaInicio?.substring(0, 5)} hs`}
                                        </div>
                                    </div>
                                </div>
                            </Col>
                        ))}
                    </Row>
                )}
            </div>
        </div>
    );
};

export default HomePage;